import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Link2, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { Field, LogoField, SectionCard, absoluteLogoUrl, publicLandingUrl, useDirtyTracker } from "./_shared";
import { QRBlock } from "./QRBlock";
import { AddPubDialog } from "./AddPubDialog";
import { LinkedPubEditor } from "./LinkedPubEditor";
import { SplitFromGroupButton } from "./SplitFromGroupButton";

/* ---------------------------------------------------------------------------
 * Pub Group editor: parent Group card + linked pubs list.
 * ------------------------------------------------------------------------- */
export function PubGroupEditor({ client, pubs, onSaved, onDeleted, onLive, onReloadPubs }) {
  const [name, setName] = useState(client.name || "");
  const [contactName, setContactName] = useState(client.contact_name || "");
  const [contactPhone, setContactPhone] = useState(client.contact_phone || "");
  const [kpiUrl, setKpiUrl] = useState(client.kpi_url || "");
  const [notes, setNotes] = useState(client.notes || "");
  const [color, setColor] = useState(client.primary_color || "#0038FF");
  const [logoUrl, setLogoUrl] = useState(client.logo_url || null);
  // iter143 — Automated Gameweek Engine (super-admin only).
  const [gameweek, setGameweek] = useState(client.current_gameweek_number ?? "");
  const [matchCount, setMatchCount] = useState(client.match_count ?? "");
  const [dayCount, setDayCount] = useState(client.day_count ?? "");
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [focusedPubId, setFocusedPubId] = useState(null);
  const [focusedPubDirty, setFocusedPubDirty] = useState(false);

  // iter76 — track unsaved edits in the Group details form. The banner
  // shows an amber pulse-dot whenever any field diverges from the last
  // committed values. On successful save (below) we call `commit()` to
  // clear the dot.
  const {
    isDirty: groupDetailsDirty,
    commit: commitGroupDetails,
  } = useDirtyTracker({
    name, contactName, contactPhone, kpiUrl, notes, color, logoUrl,
  });

  const focusedPub = pubs.find((p) => p.id === focusedPubId) || null;

  useEffect(() => {
    // Priority: an explicitly-focused pub (what an end user sees on that
    // pub's /[slug] landing). If no pub is focused (or no pubs exist
    // yet), fall back to the group card being edited so the preview still
    // reflects the group's brand as it changes.
    if (focusedPub) {
      onLive?.({
        name: focusedPub.name,
        town: focusedPub.town,
        // Prefer the pub's own colour, but the group's edited colour wins
        // when the pub hasn't overridden it — keeps colour edits visible.
        color: focusedPub.primary_color || color,
        logoUrl: focusedPub.logo_url || logoUrl,
        slug: focusedPub.slug,
      });
    } else {
      onLive?.({ name, town: null, color, logoUrl, slug: null });
    }
  }, [focusedPub, name, color, logoUrl, onLive]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}`, {
        name, contact_name: contactName, contact_phone: contactPhone,
        kpi_url: kpiUrl, notes, primary_color: color,
        current_gameweek_number: gameweek === "" ? null : Math.max(0, Number(gameweek) || 0),
        match_count: matchCount === "" ? null : Math.max(0, Number(matchCount) || 0),
        day_count: dayCount === "" ? null : Math.max(0, Number(dayCount) || 0),
      });
      toast.success("Group saved");
      // Clear the "unsaved edits" dot now that the server has the change.
      commitGroupDetails();
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete Group "${client.name}" and its ${pubs.length} pub(s)? Participants will be unlinked.`)) return;
    try {
      await api.delete(`/clients/${client.id}`);
      toast.success("Group deleted");
      onDeleted?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deletePub = async (p) => {
    if (!window.confirm(`Remove pub "${p.name}" from this group?`)) return;
    try { await api.delete(`/pubs/${p.id}`); toast.success("Pub removed"); onReloadPubs?.(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid={`edit-group-${client.id}`}>
      <SectionCard
        title={name || client.name || "Pub Group"}
        subtitle={`Pub Group · ${pubs.length} linked pub${pubs.length === 1 ? "" : "s"}`}
        logoUrl={logoUrl ? absoluteLogoUrl(logoUrl) : null}
        accentColor={color}
        testId="group-details-section"
        collapsible
        defaultCollapsed
        dirty={groupDetailsDirty}
      >
        <LogoField
          current={logoUrl}
          endpointPost={`/clients/${client.id}/logo`}
          endpointDelete={logoUrl ? `/clients/${client.id}/logo` : null}
          onUpload={(data) => setLogoUrl(data.logo_url || null)}
          testId="group-logo"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Group name"><Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="group-name" className="h-10 rounded-sm border-zinc-300" /></Field>
          <Field label="Brand colour">
            <div className="flex items-center gap-2">
              <input type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} className="h-10 w-14 rounded-sm border border-zinc-300 cursor-pointer p-0.5" />
              <Input value={color} onChange={(e) => setColor(e.target.value)} className="h-10 rounded-sm border-zinc-300 font-mono uppercase text-sm" />
            </div>
          </Field>
          <Field label="Group contact name"><Input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="group-contact-name" className="h-10 rounded-sm border-zinc-300" /></Field>
          <Field label="Group contact phone"><Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} data-testid="group-contact-phone" className="h-10 rounded-sm border-zinc-300" /></Field>
          <Field label="Group KPI dashboard link">
            <Input value={kpiUrl} onChange={(e) => setKpiUrl(e.target.value)} placeholder="https://kpi.example.com/group" data-testid="group-kpi" className="h-10 rounded-sm border-zinc-300" />
          </Field>
          <Field label="Notes" testId="group-notes-field">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="group-notes" className="min-h-[80px] rounded-sm border-zinc-300" />
          </Field>
        </div>

        {/* iter143 — Automated Gameweek Engine (super-admin only). One
            counter per client; every sibling pub reads the same values. */}
        <div className="pt-6 mt-2 border-t border-zinc-100">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">
            Automated Gameweek Engine <span className="text-zinc-400 normal-case tracking-normal font-medium">· super-admin only</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Current gameweek #" hint="Auto-increments on close season">
              <Input type="number" min="0" max="9999" value={gameweek} onChange={(e) => setGameweek(e.target.value)} data-testid="group-gameweek" className="h-10 rounded-sm border-zinc-300 tabular-nums" />
            </Field>
            <Field label="Match count" hint="Default 6">
              <Input type="number" min="0" max="999" value={matchCount} onChange={(e) => setMatchCount(e.target.value)} data-testid="group-match-count" className="h-10 rounded-sm border-zinc-300 tabular-nums" />
            </Field>
            <Field label="Day count" hint="Default 3">
              <Input type="number" min="0" max="999" value={dayCount} onChange={(e) => setDayCount(e.target.value)} data-testid="group-day-count" className="h-10 rounded-sm border-zinc-300 tabular-nums" />
            </Field>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title={`Linked pubs (${pubs.length})`}
        description="Every pub gets its own /[slug] landing and downloadable QR code."
        testId="linked-pubs-section"
        collapsible
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-zinc-500">Click a pub&apos;s banner to open its data-input form.</p>
          <button type="button" onClick={() => setAddOpen(true)} data-testid="group-add-pub" className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-sm px-3 h-9">
            <Plus className="h-3.5 w-3.5" /> Add pub
          </button>
        </div>
        {pubs.length === 0 ? (
          <p className="text-sm text-zinc-500" data-testid="group-pubs-empty">No pubs yet. Add the first one to generate its landing + QR.</p>
        ) : (
          // iter78 — each linked pub is its own identity-banner SectionCard.
          // Clicking the banner (logo + name + subtitle area) toggles the
          // full data-input form INLINE below. Only one pub can be open at
          // a time — clicking another pub swaps focus. Action buttons
          // (Open / Manage / Split / Delete) live in the actions slot so
          // they don't trigger the accordion toggle when clicked.
          <div className="space-y-3" data-testid="group-pubs-list">
            {pubs.map((p) => {
              const isOpen = focusedPubId === p.id;
              const isFocusedDirty = isOpen && focusedPubDirty;
              const rowActions = (
                <>
                  <a href={publicLandingUrl(p.slug)} target="_blank" rel="noreferrer" title="Open landing" data-testid={`group-pub-open-${p.slug}`} className="text-xs font-semibold text-zinc-600 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-sm px-2 h-8 inline-flex items-center gap-1"><Link2 className="h-3.5 w-3.5" /> Open</a>
                  <a href={`/c/${p.slug}/editor`} target="_blank" rel="noreferrer" title="Manage back office" data-testid={`manage-back-office-${p.slug}`} className="text-xs font-semibold text-white bg-zinc-950 hover:bg-black rounded-sm px-2 h-8 inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Manage</a>
                  <SplitFromGroupButton pub={p} onSplit={() => onReloadPubs?.()} />
                  <button type="button" onClick={() => deletePub(p)} title="Remove pub" className="text-xs font-semibold text-red-600 hover:bg-red-600 hover:text-white border border-red-200 hover:border-red-600 rounded-sm px-2 h-8 inline-flex items-center gap-1" data-testid={`group-pub-delete-${p.slug}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              );
              return (
                <SectionCard
                  key={p.id}
                  testId={`linked-pub-card-${p.slug}`}
                  title={p.name}
                  subtitle={`${p.town || "No town"} · /${p.slug}`}
                  logoUrl={p.logo_url ? absoluteLogoUrl(p.logo_url) : null}
                  accentColor={p.primary_color}
                  actions={rowActions}
                  collapsible
                  controlled
                  open={isOpen}
                  onToggle={(next) => {
                    setFocusedPubId(next ? p.id : null);
                    if (!next) setFocusedPubDirty(false);
                  }}
                  dirty={isFocusedDirty}
                >
                  {isOpen && (
                    <LinkedPubEditor
                      key={p.id}
                      pub={p}
                      onSaved={() => onReloadPubs?.()}
                      onDeleted={() => onReloadPubs?.()}
                      onLive={onLive}
                      onDirtyChange={setFocusedPubDirty}
                    />
                  )}
                </SectionCard>
              );
            })}
          </div>
        )}
      </SectionCard>

      <div className="flex items-center justify-between pt-1">
        <button type="button" onClick={del} data-testid="group-delete" className="text-xs font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1">
          <Trash2 className="h-3.5 w-3.5" /> Delete group
        </button>
        <Button type="submit" disabled={saving} data-testid="group-save" className="rounded-sm bg-zinc-950 hover:bg-black text-white h-10 px-6">
          {saving ? "Saving…" : "Save group"}
        </Button>
      </div>

      <AddPubDialog open={addOpen} onOpenChange={setAddOpen} clientId={client.id} onCreated={() => onReloadPubs?.()} />
    </form>
  );
}
